import _ from 'lodash';
import React, { useEffect, useState, useContext, useRef } from 'react';
import { FormInputContainer, FormContext } from 'blaze-form';
import { Card, Badge, ButtonGroup, Button } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import ApiService from '../../services/ApiService';
import CurrencyItem from '../form/misc/CurrencyItem';
import FormInputVehicleSaleSearch from '../form/control/FormInputVehicleSaleSearch';
import { getListItemId } from '../../util/form';
import { modal } from '../modal/ModalEmitter';
import { statusColors, getStatus } from '../../util/vehicle';
import DealContext from './DealContext';
import toastr from '../toastr';

const FormInputVehicleSelection = ({ onChange, ...props }) => {
  const { manager, ee, isReadonly, handleSubmit } = useContext(FormContext);

  const { states } = useContext(DealContext);

  const [vehicle, setVehicle] = useState(null);
  const [showEntry, setShowEntry] = useState(false);
  const [vehicleSelection, setVehicleSelection] = useState(props.value);

  // Loading vars
  const [fetchingVehicle, setFetchingVehicle] = useState(true);
  const [checkingVehicle, setCheckingVehicle] = useState(false);
  const [changingVehicle, setChangingVehicle] = useState(false);

  const isMountedRef = useRef(null);
  const firstUpdate = useRef(true);

  const quoteId = manager.itemId;

  const fetchVehicle = vehicleId => {
    if (!vehicleId) {
      return;
    }

    setFetchingVehicle(true);
    return ApiService.get(`crm/vehicle/${encodeURIComponent(vehicleId)}`)
      .then(response => {
        // TODO Apparently an anti-pattern
        if (isMountedRef.current) {
          setVehicle(response.data);
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setFetchingVehicle(false);
        }
      });
  };

  const handleVehicleSelection = (suggestion, value) => {
    const vehicleId = getListItemId(value);
    if (vehicleId === vehicleSelection) {
      return;
    }

    setCheckingVehicle(true);
    ApiService.get(`crm/vehicle/${encodeURIComponent(vehicleId)}/availability`)
      .then(response => {
        setVehicleSelection(vehicleId);
      })
      .catch(({ response }) => {
        toastr.error('Invalid Selection', _.get(response, 'data.message'));
      })
      .finally(() => {
        setCheckingVehicle(false);
      });
  };

  const handleChange = event => {
    event.stopPropagation();
    setShowEntry(true);
  };

  const handleCancel = () => {
    setShowEntry(false);

    if (vehicle) {
      setVehicleSelection(vehicle.itemid);
    }
  };

  const handleClickCard = () => {
    modal.open('vehicleSpecSheet', { lookupKey: vehicleSelection });
  };

  const handleInfo = event => {
    event.stopPropagation();

    const callback = response => {
      const newCost = _.get(response, 'data.cost');
      const currentCost = manager.getValue('data.costsToDate');
      if (newCost !== currentCost) {
        updateCostOnForm(newCost);
      }
    };

    modal.open('quoteCostToDate', { quoteId, callback });
  };

  const handleStatusUpdate = payload => {
    // Refetch if the status changed
    if (payload.vehicleId === vehicleSelection) {
      fetchVehicle(payload.vehicleId);
    }
  };

  const handleConfirmVehicle = () => {
    // New Entry so quoteId isn't set (wont matter with noSubmit on though)
    if (!quoteId) {
      onChange('vehicleId', vehicleSelection);
      fetchVehicle(vehicleSelection);
      return;
    }

    setChangingVehicle(true);

    // TODO Should backend save the new cost?
    return ApiService.put(`/crm/quote/${quoteId}/change-vehicle`, { vehicleId: vehicleSelection })
      .then(() => {
        // return ApiService.put(`/db/EQS1/${quoteId}`, { vehicleId: vehicleSelection }).then(() => {
        fetchVehicle(vehicleSelection)
          .then(() => {
            setShowEntry(false);
          })
          .finally(() => {
            setChangingVehicle(false);
          });
      })
      .catch(() => {
        toastr.error('Error', 'Failed to change vehicle');
      });
  };

  const updateCostOnForm = newCost => {
    const currentStatus = manager.getValue('statusId');
    const currentCost = manager.getValue('data.costsToDate');
    const currentState = states.find(s => s.id === currentStatus);

    if (!currentState) {
      console.warn(`Unknown status id: ${currentStatus}`);
      return;
    }

    // Don't update the form in Posted, Closed, Cancelled states
    if (/(?:POSTED|CLOSED|CANCELLED)/.test(currentState.logicalName)) {
      return;
    }

    manager.formHandler.current.updateForm({ 'data.costsToDate': String(newCost) });
    modal.open({
      hidePrint: true,
      size: 'sm',
      style: 'center',
      title: 'Data Updated',
      message: (
        <div>
          The Costs To-date has changed from{' '}
          <strong>
            <CurrencyItem>{currentCost}</CurrencyItem>
          </strong>{' '}
          to{' '}
          <strong>
            <CurrencyItem>{newCost}</CurrencyItem>
          </strong>
          . This may impact other calculations.
        </div>
      ),
    });

    handleSubmit();
  };

  useEffect(() => {
    if (showEntry) {
      setTimeout(() => {
        const el = document.querySelector('.vehicle-select input');
        el.focus();
        el.select();
      }, 300);
    }
  }, [showEntry]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (vehicle) {
      const currentCost = parseFloat(manager.getValue('data.costsToDate'));
      const totalCost = String(vehicle['Total Cost']) || '0';
      const newCost = parseFloat(totalCost.replace(/[$,]+/g, ''));

      if (currentCost !== newCost && !isNaN(newCost) && newCost !== 0) {
        updateCostOnForm(newCost);
      }
    }

    // Keep VIN up-to-date as form data
    manager.setValue('vin', _.get(vehicle, 'VIN', ''));
    manager.setValue('condition', getStatus(_.get(vehicle, 'New-Used', '')));
  }, [vehicle]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchVehicle(props.value);

    if (ee) {
      ee.on('vehicle.status', (...args) => {
        setTimeout(() => {
          handleStatusUpdate(...args);
        }, 10);
      });
    }

    return () => {
      isMountedRef.current = false;
      if (ee) {
        ee.off('vehicle.status');
      }
    };
  }, []);

  if (fetchingVehicle) {
    return (
      <Card className="mb-3" style={{ height: '119px' }}>
        <Card.Body style={{ padding: '1rem' }}>
          <Skeleton className="ml-1" style={{ width: '20px', float: 'right' }} />
          <Skeleton style={{ width: '20px', float: 'right' }} />
          <Skeleton style={{ width: '50%' }} />
          <Skeleton style={{ width: '75%' }} />
          <Skeleton style={{ width: '40%' }} />
        </Card.Body>
      </Card>
    );
  }

  if (showEntry || !vehicle) {
    return (
      <FormInputContainer>
        <Card className="vehicle-select">
          <Card.Body style={{ padding: '1rem' }}>
            <FormInputVehicleSaleSearch
              {...props}
              onChange={() => {}}
              onSuggestionSelected={handleVehicleSelection}
              value={vehicleSelection}
            />
          </Card.Body>
          <ButtonGroup style={{ width: '100%', marginTop: '-23px' }} size="sm">
            {changingVehicle !== true && (
              <Button variant="secondary" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            <Button
              disabled={changingVehicle || (vehicle && vehicleSelection === vehicle.itemid) || checkingVehicle}
              onClick={handleConfirmVehicle}
              size="sm"
              variant="primary"
            >
              {changingVehicle === true ? 'Please Wait' : 'Confirm'}
            </Button>
          </ButtonGroup>
        </Card>
      </FormInputContainer>
    );
  }

  const status = getStatus(vehicle.Status);
  const condition = getStatus(vehicle['New-Used']);

  return (
    <FormInputContainer>
      {vehicle && (
        <Card className="vehicle-select-data" onClick={handleClickCard} style={{ cursor: 'pointer' }}>
          <div className="card-heading">Unit</div>
          <Card.Body style={{ padding: '1rem' }}>
            {vehicle.Status && (
              <>
                <Badge
                  className="float-right mt-1"
                  title={vehicle.Status}
                  style={{ color: '#fff', padding: '3x 5px', backgroundColor: statusColors[status] }}
                >
                  {status}
                </Badge>
                <Badge
                  className="float-right mt-1 mr-1"
                  title={vehicle['New-Used']}
                  style={{
                    color: '#fff',
                    padding: '3x 5px',
                    backgroundColor: statusColors[condition],
                  }}
                >
                  {condition}
                </Badge>
              </>
            )}
            <div>
              <b>{vehicle.itemid}</b> ({vehicle.Type})
            </div>
            {(vehicle.Year || vehicle.Make || vehicle.Model) && (
              <div style={{ maxWidth: '275px' }}>
                {vehicle.Year} {vehicle.Make} {vehicle.Model}
              </div>
            )}
            {vehicle['Total Cost'] && <CurrencyItem>{vehicle['Total Cost']}</CurrencyItem>}
          </Card.Body>
          <ButtonGroup size="sm">
            <Button variant="secondary" onClick={handleInfo} style={{ width: '50%' }}>
              Costs
            </Button>
            {isReadonly === false && (
              <Button variant="info" onClick={handleChange} style={{ width: '50%' }}>
                Change
              </Button>
            )}
          </ButtonGroup>
        </Card>
      )}
    </FormInputContainer>
  );
};

export default FormInputVehicleSelection;
